import { useContext } from 'react';
import { RendererContext } from '../../contexts/RendererContext';
import { keyBy } from 'lodash/fp';
import type { ClusterSegment } from '@pointorama/potree-shared';

/** Subset of PPCPointCloudGeometryNode from potree_fork */
interface PointCloudNode {
  geometryNode?: {
    geometry?: {
      attributes: {
        segment?: {
          array: Uint32Array | number[];
        };
      };
    };
  };
}

export const usePointClusterProperties = (segments: ClusterSegment[]) => {
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;

  // Index point clouds by identifier (MongoDB ID) since that's what segments store
  const pointCloudsByIdentifier = keyBy('identifier', viewer?.scene.pointclouds);

  // Try to get point count from segmentList metadata (from .pcsh files)
  // If that's not available, count from actual loaded point data
  let pointCount = 0;

  // First, check if we have metadata available
  const hasMetadata = segments.every((segment) => {
    const pointCloud = pointCloudsByIdentifier[segment.pointcloudId];
    return pointCloud && pointCloud.segmentList && pointCloud.segmentList.length > 0 && pointCloud.segmentList[segment.segmentId];
  });

  if (hasMetadata) {
    // Use metadata from .pcsh files (faster)
    pointCount = segments.reduce((sum, segment) => {
      const pointCloud = pointCloudsByIdentifier[segment.pointcloudId];
      const segmentData = pointCloud.segmentList[segment.segmentId] as { voxelCount?: number } | undefined;
      return sum + (segmentData?.voxelCount || 0);
    }, 0);
  } else {
    // Fallback: Count points from loaded geometry
    const segmentCounts = new Map<string, number>();

    segments.forEach((segment) => {
      const pointCloud = pointCloudsByIdentifier[segment.pointcloudId];
      if (!pointCloud) return;

      // Count points in all loaded nodes
      (pointCloud.visibleNodes as PointCloudNode[] | undefined)?.forEach((node: PointCloudNode) => {
        const geometry = node.geometryNode?.geometry;
        if (!geometry) return;

        // Check if this geometry has segment data
        const segmentAttribute = geometry.attributes.segment;
        if (!segmentAttribute) return;

        const segmentData = segmentAttribute.array;

        // Count points matching this segment ID
        for (let i = 0; i < segmentData.length; i++) {
          if (segmentData[i] === segment.segmentId) {
            const key = `${segment.pointcloudId}-${segment.segmentId}`;
            segmentCounts.set(key, (segmentCounts.get(key) || 0) + 1);
          }
        }
      });
    });

    // Sum up all segment counts
    pointCount = Array.from(segmentCounts.values()).reduce((sum, count) => sum + count, 0);
  }

  return { pointCount };
};
